// src/components/support/Support.js
import React from 'react';
import {
    Calendar,
    FileText,
    Bus,
    CreditCard,
    Activity,
    LogOut,
    Headphones,
    ChevronRight,
    Baby,
    Key,
} from 'lucide-react';
import { useRequestProcessor } from '../../requestProcessor';
import { useAtom } from 'jotai';
import { activeChildDetail, allowedService, requestType, TabsAtom } from '../../atomstore/store';
import { fetchRequestTypes } from '../../queryfunctions/queryfunctions';
import SpinnerLoading from '../common/SpinnerLoading';
import { useNavigate } from 'react-router-dom';


const Support = () => {
    const navigate = useNavigate()
    // Always visible options
    const staticOptions = [
        {
            serviceKey: "Finance",
            icon: CreditCard,
            title: 'Finance',
            desc: 'Invoices, payments & certificates',
            screen: 'finance',
            gradient: 'from-green-400 to-teal-500'
        },
        {
            serviceKey: "Contact",
            icon: Headphones,
            title: 'Contact Us',
            desc: 'Ticket support',
            screen: 'contact',
            gradient: 'from-blue-400 to-cyan-500'
        },
        {
            serviceKey: "DoorPin",
            icon: Key,
            title: 'Door PIN',
            desc: 'View and regenerate access PIN',
            screen: 'door-pin',
            gradient: 'from-amber-400 to-orange-500'
        },
        // {
        //     icon: ClipboardList,
        //     title: 'Past Requests',
        //     desc: 'Past Requests',
        //     screen: 'past-request',
        //     gradient: 'from-red-400 to-black'
        // }
    ];

    // Dynamic options based on API allowed services
    const dynamicOptions = [
        {
            serviceKey: "ChildPause",
            icon: Calendar,
            title: 'Take a Break',
            desc: 'Pause services temporarily',
            screen: 'take-break',
            gradient: 'from-purple-400 to-pink-500'
        },
        {
            serviceKey: "Program Change",
            icon: FileText,
            title: 'Change Program',
            desc: 'Switch to a different program',
            screen: 'change-program',
            gradient: 'from-orange-400 to-red-500'
        },
        {
            serviceKey: "Transport",
            icon: Bus,
            title: 'Transport',
            desc: 'Start or stop transport service',
            screen: 'transport',
            gradient: 'from-yellow-400 to-orange-500'
        },
        {
            serviceKey: "Center Shift",
            icon: Activity,
            title: 'Shift Center',
            desc: 'Request a center transfer',
            screen: 'shift-center',
            gradient: 'from-indigo-400 to-purple-500'
        },
        {
            serviceKey: "ChildWithdraw",
            icon: LogOut,
            title: 'Withdraw Child',
            desc: 'Discontinue enrollment',
            screen: 'withdraw',
            gradient: 'from-red-400 to-pink-500'
        },
        {
            serviceKey: "Extended Daycare",
            icon: Baby,
            title: 'Extended Daycare',
            desc: 'Extend Daycare',
            screen: 'extend-daycare',
            gradient: 'from-blue-400 to-cyan-500'
        }
        // {
        //     serviceKey: "Time Extension",
        //     icon: Calendar,
        //     title: 'Time Extension',
        //     desc: 'Extend service hours',
        //     screen: 'time-extension',
        //     gradient: 'from-teal-400 to-blue-500'
        // }
    ];

    const { useQueryWrapper } = useRequestProcessor();
    const [types, setType] = useAtom(requestType);
    const [, setTabs] = useAtom(TabsAtom);
    const [allowedServices, setAllowedServices] = useAtom(allowedService);
    const [activeChild] = useAtom(activeChildDetail);

    let { isLoading } = useQueryWrapper(
        ["getRequestType", activeChild?.id],
        () => {
            return fetchRequestTypes(setAllowedServices, setType, setTabs);
        },
        {
            onSuccess: (data) => {
                console.log("[Support] Request types:", data);
            }
        }
    );

    const normalizePendingKey = (value) =>
        String(value ?? '')
            .toLowerCase()
            .replace(/[\s_-]+/g, '');

    const hasPendingRequestForService = (serviceKey) => {
        console.log("service key", serviceKey)
        if (!types?.pending_requests?.length) return false;

        const aliasesByServiceKey = {
            // API can return "Leaves" or "Child Pause" for Take a Break requests
            ChildPause: ['Leaves', 'Child Pause'],
            // API can return "Child Attrition" for Withdraw Child requests
            ChildWithdraw: ['Child Attrition'],
        };

        const candidates = [serviceKey, ...(aliasesByServiceKey[serviceKey] || [])].map(normalizePendingKey);

        return types.pending_requests.some((req) => {
            const reqType = normalizePendingKey(req?.type);
            return candidates.some((c) => reqType.includes(c));
        });
    };


    const filteredDynamicOptions = dynamicOptions.filter(item =>
        allowedServices?.includes(item.serviceKey)
    );

    const allOptions = [...filteredDynamicOptions, ...staticOptions];



    const handleClick = (item) => {
        console.log("service key inside handleClick", item)
        if (hasPendingRequestForService(item.serviceKey)) {
            navigate('/past-request');
        } else {
            navigate('/' + item.screen);
        }
    };


    return (
        <div className="p-4 space-y-3">
            {isLoading && <SpinnerLoading />}
            <div className='flex items-center justify-between'>

                <h2 className="text-2xl font-bold text-gray-800 mb-2">Support</h2>
                <button className='bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-xl px-3 py-1 font-medium' onClick={() => navigate('/past-request')}>Past requests</button>
            </div>

            {allOptions.map((item, idx) => (
                <button
                    key={idx}
                    onClick={() => handleClick(item)}
                    className="w-full bg-white rounded-2xl p-4 shadow-sm border border-gray-100 active:scale-95 transition"
                >
                    <div className="flex items-center space-x-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center bg-gradient-to-br ${item.gradient} shadow-md`}>
                            <item.icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                            <p className="font-semibold text-gray-800">{item.title}</p>
                            <p className="text-sm text-gray-500">{item.desc}</p>
                        </div>
                        <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                </button>
            ))}
        </div>
    );
};

export default Support;
